import { createApi } from '@reduxjs/toolkit/query/react';

import { createApiBaseQuery, Methods } from '../shared';

import { ACCESS_RIGHTS_CHUNK } from './const';
import {
  AccessRightsEndpoints as Endpoints,
  type ApplyAccessRightsTemplateRequest,
  type ApplyAccessRightsTemplateResponse,
  type DeleteAccessRightsTemplateRequest,
  type DeleteAccessRightsTemplateResponse,
  type GetAccessRightsTemplateRequest,
  type GetAccessRightsTemplateResponse,
  type GetAccessRightsTemplatesRequest,
  type GetAccessRightsTemplatesResponse,
  type GetUserAccessRightsRequest,
  type GetUserAccessRightsResponse,
  type UpdateAccessRightsModulesRequest,
  type UpdateAccessRightsModulesResponse,
  type UpdateAccessRightsTemplateByUserIdRequest,
  type UpdateAccessRightsTemplateByUserIdResponse,
  type UpdateAccessRightsTemplateRequest,
  type UpdateAccessRightsTemplateResponse,
  type UpdateAccessRightsTemplatesRequest,
  type UpdateAccessRightsTemplatesResponse,
  type UpdateUserAccessRightsRequest,
  type UpdateUserAccessRightsResponse,
} from './types';

export const accessRightsApi = createApi({
  reducerPath: 'accessRightsApi',
  baseQuery: createApiBaseQuery(ACCESS_RIGHTS_CHUNK),
  endpoints: ({ query, mutation }) => ({
    [Endpoints.GetUserAccessRights]: query<
      GetUserAccessRightsResponse,
      GetUserAccessRightsRequest
    >({
      query: (params) => ({
        url: Endpoints.GetUserAccessRights,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.UpdateUserAccessRights]: mutation<
      UpdateUserAccessRightsResponse,
      UpdateUserAccessRightsRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.UpdateUserAccessRights,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.GetAccessRightsTemplate]: query<
      GetAccessRightsTemplateResponse,
      GetAccessRightsTemplateRequest
    >({
      query: (params) => ({
        url: Endpoints.GetAccessRightsTemplate,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetAccessRightsTemplates]: query<
      GetAccessRightsTemplatesResponse,
      GetAccessRightsTemplatesRequest
    >({
      query: (params) => ({
        url: Endpoints.GetAccessRightsTemplates,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.UpdateAccessRightsTemplate]: mutation<
      UpdateAccessRightsTemplateResponse,
      UpdateAccessRightsTemplateRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.UpdateAccessRightsTemplate,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.DeleteAccessRightsTemplate]: mutation<
      DeleteAccessRightsTemplateResponse,
      DeleteAccessRightsTemplateRequest
    >({
      query: (params) => ({
        url: Endpoints.DeleteAccessRightsTemplate,
        method: Methods.POST,
        params,
      }),
    }),
    [Endpoints.UpdateAccessRightsTemplateByUserId]: mutation<
      UpdateAccessRightsTemplateByUserIdResponse,
      UpdateAccessRightsTemplateByUserIdRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.UpdateAccessRightsTemplateByUserId,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.ApplyAccessRightsTemplate]: mutation<
      ApplyAccessRightsTemplateResponse,
      ApplyAccessRightsTemplateRequest
    >({
      query: ({ AccessRightsTemplateId, UserId, ...params }) => ({
        url: Endpoints.ApplyAccessRightsTemplate,
        method: Methods.POST,
        params,
        data: { AccessRightsTemplateId, UserId },
      }),
    }),
    [Endpoints.UpdateAccessRightsTemplates]: mutation<
      UpdateAccessRightsTemplatesResponse,
      UpdateAccessRightsTemplatesRequest
    >({
      query: (params) => ({
        url: Endpoints.UpdateAccessRightsTemplates,
        method: Methods.POST,
        params,
      }),
    }),
    [Endpoints.UpdateAccessRightsModules]: mutation<
      UpdateAccessRightsModulesResponse,
      UpdateAccessRightsModulesRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.UpdateAccessRightsModules,
        method: Methods.PUT,
        params,
        data,
      }),
    }),
  }),
});

export const {
  useGetUserAccessRightsQuery,
  useLazyGetUserAccessRightsQuery,
  useUpdateUserAccessRightsMutation,
  useGetAccessRightsTemplateQuery,
  useLazyGetAccessRightsTemplateQuery,
  useGetAccessRightsTemplatesQuery,
  useLazyGetAccessRightsTemplatesQuery,
  useUpdateAccessRightsTemplateMutation,
  useDeleteAccessRightsTemplateMutation,
  useUpdateAccessRightsTemplateByUserIdMutation,
  useApplyAccessRightsTemplateMutation,
  useUpdateAccessRightsTemplatesMutation,
  useUpdateAccessRightsModulesMutation,
} = accessRightsApi;
